import { useEffect, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { getAdminMarketingOverview } from "../../../../../api/adminMarketingOverview";
import { getMarketingBroadcastHistory } from "../../../../../api/adminMarketingBroadcast";
import { ComponentLoader } from "../../../../../components/loaders";
import { readAdminPageState, saveAdminPageState } from "../../../../../utils/adminPageState";
import { BroadcastHistoryPreview } from "../broadcast-history";
import { DashboardHeader, TrendChart } from "../dashboard-components";
import { DashboardWrapper } from "../dashboard.style";

const dateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultEndDate = () => dateInputValue(new Date());
const defaultOverviewStartDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return dateInputValue(date);
};

export const MarketingDashboardSection = () => {
  const location = useLocation();
  const [initialPageState] = useState(() => readAdminPageState(location.pathname, 
    { startDate: defaultOverviewStartDate(), endDate: defaultEndDate()}));
  const [startDate, setStartDate] = useState(initialPageState.startDate);
  const [endDate, setEndDate] = useState(initialPageState.endDate);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [growthError, setGrowthError] = useState("");
  const [growthRetryKey, setGrowthRetryKey] = useState(0);
  const [overviewReady, setOverviewReady] = useState(false);
  const [historyPreview, setHistoryPreview] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyReady, setHistoryReady] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyRetryKey, setHistoryRetryKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setGrowthError("");
    getAdminMarketingOverview({ startDate, endDate })
      .then((response) => { if (active) setOverview(response); })
      .catch((error) => {
        if (!active) return;
        setOverview(null);
        setGrowthError(error?.response?.data?.message || error?.message || "Marketing overview could not be loaded.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        setOverviewReady(true);
      });
    return () => { active = false; };
  }, [endDate, growthRetryKey, startDate]);

  useEffect(() => {
    saveAdminPageState(location.pathname, { startDate, endDate, });
  }, [endDate, location.pathname, startDate]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setHistoryError("");
    getMarketingBroadcastHistory({ channel: "all", segment: "all", startDate: startDate, endDate: endDate, page: 1, pageSize: 10 })
      .then((response) => {
        if (!active) return;
        setHistoryPreview(response.data);
        setHistoryTotal(response.pagination.total_items);
      })
      .catch((error) => {
        if (!active) return;
        setHistoryPreview([]);
        setHistoryTotal(0);
        setHistoryError(error?.response?.data?.message || error?.message || "Broadcast history could not be loaded.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        setHistoryReady(true);
      });
    return () => { active = false; };
  }, [startDate, endDate, historyRetryKey]);

  const changeStartDate = (event) => {
    const value = event.target.value;
    setStartDate(value);
    if (value > endDate) setEndDate(value);
  };
  const changeEndDate = (event) => {
    const value = event.target.value;
    setEndDate(value);
    if (value < startDate) setStartDate(value);
  };
  const summary = overview?.buyer_growth_summary;
  const composition = overview?.buyer_composition;
  const growthTone = Number(summary?.growth_rate_percentage || 0) < 0 ? "negative" : "success";
  const initialLoading = !overviewReady || !historyReady;

  return <DashboardWrapper>
    <DashboardHeader title="Marketing dashboard" description="Track buyer engagement, understand customer segments and coordinate timely communication." start={startDate} end={endDate} onStartChange={changeStartDate} onEndChange={changeEndDate} />
    {(initialLoading || loading)? 
    <div className="marketing-page-loader"><ComponentLoader /></div> : <>
      {loading ? <div className="overview-refresh-loader"><ComponentLoader /></div> : growthError ? <section className="content-card marketing-overview-error" role="alert"><FiAlertCircle aria-hidden="true" /><strong>Marketing overview could not be loaded</strong><span>{growthError}</span><button type="button" onClick={() => setGrowthRetryKey((current) => current + 1)}>Try again</button></section> : <div className="dashboard-grid">
        <section className="content-card">
          <div className="card-heading"><div><h2>Active buyer growth</h2><p>Active buyers across the selected date range.</p></div>{summary?.growth_rate_label && <span className={`status-pill ${growthTone}`}>{summary.growth_rate_label}</span>}</div>
          {overview?.chart_data.length ? <><TrendChart data={overview.chart_data} dataKey="active_buyers" color="#287591" valueLabel="Active buyers" />
          <div className="chart-legend"><span><i className="legend-dot" style={{ background: "#287591" }} />{summary.current_active_buyers.toLocaleString()} active buyers</span><span>{summary.net_growth_label || `${summary.net_growth_count.toLocaleString()} net growth`}</span></div></> : <div className="growth-chart-state growth-zero"><strong>{Number(summary?.current_active_buyers || 0).toLocaleString()} active buyers recorded</strong><span>{summary?.net_growth_label || "No daily chart points were returned for this date range."}</span><small>No daily chart points were returned by the API.</small></div>}
        </section>
        <section className="content-card">
          <div className="card-heading"><div><h2>Buyer segments</h2><p>Percentage of active registered and guest buyers.</p></div>{composition && <span className="composition-total"><strong>{composition.total_active_buyers.toLocaleString()}</strong> active</span>}</div>
          {composition ? <div className="buyer-composition">
            <div className="composition-bar" role="img" aria-label={`${composition.registered_buyers_percentage}% registered buyers and ${composition.guest_buyers_percentage}% guest buyers`}><span className="registered" style={{ width: `${composition.registered_buyers_percentage}%` }} /><span className="guest" style={{ width: `${composition.guest_buyers_percentage}%` }} /></div>
            <div className="composition-list">
              <article><span className="composition-dot registered" /><div><span>Registered buyers</span><strong>{composition.registered_buyers_percentage.toFixed(1)}%</strong><small>{composition.registered_buyers_count.toLocaleString()} buyers</small></div></article>
              <article><span className="composition-dot guest" /><div><span>Guest buyers</span><strong>{composition.guest_buyers_percentage.toFixed(1)}%</strong><small>{composition.guest_buyers_count.toLocaleString()} buyers</small></div></article>
            </div>
          </div> : <div className="composition-state"><span>No buyer composition was returned.</span></div>}
        </section>
      </div>}
      <BroadcastHistoryPreview history={historyPreview} totalItems={historyTotal} loading={loading} error={historyError} onRetry={() => setHistoryRetryKey((current) => current + 1)} />
    </>}
  </DashboardWrapper>;
};
